import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import dayjs from 'dayjs';
import { useScheduleBoardContext } from 'pos-client/components/scheduleBoard/context/ScheduleBoardContext.tsx';
import { searchFilterJobs } from 'pos-client/components/scheduleBoard/utils/searchFilterJobs.ts';
import { useNotifications } from 'pos-client/hooks/useNotifications.tsx';
import { isToday } from 'pos-client/utils/dayjs.ts';
import { trpc } from 'pos-client/utils/trpc.ts';
import type { AppConfig } from 'pos-shared/models/appConfigSchema.ts';
import type {
  LocationTypeRequestModel,
  ScheduleBoardJobModel,
  ServiceTypesRequestModel,
} from 'pos-shared/models/schedulingSchema.ts';
import { validate } from 'uuid';

import { useSelectedLocationTimeZoneOffset } from './useSelectedLocationTimeZoneOffset.ts';

export const useJobsAndBlockedSlots = (
  assetIds: string[],
  config: AppConfig | undefined
) => {
  const { t } = useTranslation();

  const { showError } = useNotifications();

  const {
    selectedLocation,
    serviceTypeFilter,
    selectedDate,
    searchTerm,
    setBoardData,
    setIsScheduleBoardHeaderActionLoading,
  } = useScheduleBoardContext();

  const { timeZoneOffset } = useSelectedLocationTimeZoneOffset();

  const {
    data: jobsAndBlockedSlotsData,
    isFetching: jobsAndBlockedSlotsLoading,
  } = trpc.findScheduleBoardJobsAndBlockedSlots.useQuery(
    {
      assetIds: assetIds,
      serviceLocationId: selectedLocation?.id ?? '',
      dateOfService: dayjs(selectedDate).format('YYYY-MM-DD'),
      serviceTypes: serviceTypeFilter as ServiceTypesRequestModel,
      locationType:
        (selectedLocation?.type as LocationTypeRequestModel) ?? 'Hub',
      timeZoneOffset,
    },
    {
      refetchInterval:
        isToday(selectedDate) &&
        1000 * 60 * (config?.scheduleBoardRefreshInterval ?? 5),
      refetchOnMount: false,
      enabled: !!selectedLocation?.id && !!assetIds.length,
      initialData: [],
      onError: () => {
        setBoardData([]);
        showError(
          t('common.notification.error.message', {
            action: 'load',
            entity: 'jobs',
          })
        );
      },
      onSettled: () => {
        setIsScheduleBoardHeaderActionLoading(false);
      },
    }
  );

  const jobsAndBlockedSlotsSanitized = useMemo(() => {
    if (!jobsAndBlockedSlotsData) return [];
    const sanitizedData = jobsAndBlockedSlotsData
      .map((element) => {
        const newElement = { ...element };
        if (newElement?.startDate?.endsWith('Z')) {
          newElement.startDate = newElement?.startDate?.slice(0, -1);
        }
        if (newElement?.endDate?.endsWith('Z')) {
          newElement.endDate = newElement?.endDate.slice(0, -1);
        }
        return newElement;
      })
      .filter((item) => {
        if (item?.text?.toLowerCase().includes('blocked')) return true;
        const orderItem = item as ScheduleBoardJobModel;
        const orderIdIsValid =
          orderItem?.orderId &&
          validate(orderItem?.orderId) &&
          orderItem.jobId !== '00000000-0000-0000-0000-000000000000';
        const jobIdIsValid =
          orderItem?.jobId &&
          validate(orderItem.jobId) &&
          orderItem.jobId !== '00000000-0000-0000-0000-000000000000';

        return orderIdIsValid || jobIdIsValid;
      });

    return sanitizedData as ScheduleBoardJobModel[] | [];
  }, [jobsAndBlockedSlotsData]);

  const jobsAndBlockedSlotsSearchFiltered = useMemo(() => {
    return searchFilterJobs(jobsAndBlockedSlotsSanitized, searchTerm);
  }, [jobsAndBlockedSlotsSanitized, searchTerm]);

  setBoardData(
    jobsAndBlockedSlotsSearchFiltered as ScheduleBoardJobModel[] | []
  );

  return {
    jobsAndBlockedSlots: jobsAndBlockedSlotsSearchFiltered,
    jobsAndBlockedSlotsLoading,
  };
};
